import {
  ActionType,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { DetailsPanelId, PageId, PageRegistrationConfig, RESOURCE_ROUTE } from "../../constants/pluginConstants";
import { ListingTestIds, MainTargetId, MonochromeIconIds, ttlOneMinCaching } from "../../constants/uiConstants";
import {
  PluggableDatabaseSummary,
  PluggableDatabaseSummaryCollection,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import {
  getStatusInfo,
  optimizedRetryOption,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { ExaDbCreatePdbProps, newExaDbCreatePdb } from "../../operations/ExaDb/ExaDbCreatePdb";
import { ExaDbDeletePdbProps, newExaDbDeletePdb } from "../../operations/ExaDb/ExaDbDeletePdb";
import { chainFunctions, getAzureLocationName, getOciRegion } from "../../utils";

/**
 * TODO
 * This interface is implement to include the last status and details.
 * Api has not expose these two fields for now. When it will, we might remove this interface.
 */
interface PluggableDatabaseSummaryExt extends PluggableDatabaseSummary {
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
}

export interface ExaDbPdbsListProps {
  databaseId: string;
  location: string;
}

export enum ColumnIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status",
}

export enum ColumnTestIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status",
}

export enum ActionIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

export enum ActionBarTestIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.EXADB_PDB_DETAILS].map(config => config.key);
const pageId = PageId.EXADB_CDB_DETAILS;

export const ExaDbPdbsList = (
  { databaseId, location }: ExaDbPdbsListProps,
): JSX.Element => {
  const { locale } = React.useContext<Settings>(SettingsContext);

  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { resourceGroup, subscriptionId } = parseId(databaseId);

  const [selection, setSelection] = React.useState<PluggableDatabaseSummary[]>([]);

  const cdbResGroup = decodeURIComponent(resourceGroup || "");

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const { perPageItemsCount } = useRowCount();

  const { response, loading, refresh } = useQueryCall({
    wait: !databaseId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listPluggableDatabases,
    options: {
      args: {
        subscriptionId,
        resourceGroupName: cdbResGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        databaseId,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadPluggableDatabase(),
      },
    },
  });

  const pdbs = response
    && responseItemstoArray<PluggableDatabaseSummaryCollection, PluggableDatabaseSummary>(response);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => pdbs, [JSON.stringify(pdbs)]);

  const getLocation = (value: PluggableDatabaseSummary): JSX.Element => (
    <div>{getAzureLocationName(value?.location || "")}</div>
  );

  const getStatus = (value: PluggableDatabaseSummaryExt): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.Name,
      testId: ColumnTestIds.Name,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.name}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXADB_PDB_DETAILS][0].panelPath}?location=${value.location}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
        />
      ),
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      itemProp: ColumnIds.Location,
      name: Messages.labels.location(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: PluggableDatabaseSummaryExt,
        b: PluggableDatabaseSummaryExt,
      ) => statusSortComparator<PluggableDatabaseSummaryExt>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  const { trigger: triggerCreatePdb } = useOperation<ExaDbCreatePdbProps>(newExaDbCreatePdb);
  const { trigger: triggerDeletePdb } = useOperation<ExaDbDeletePdbProps>(newExaDbDeletePdb);

  const { closePanels } = useSidePanel();

  const dbActions: ActionType[] = [
    {
      key: ActionIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        listingComponentRef?.resetTableSelection();
        trackActionClick(ActionIds.Create, pageId, DetailsPanelId.PLUGGABLEDATABASES);
        triggerCreatePdb({
          databaseId,
          targetId: MainTargetId,
          location,
          onExecute: refresh,
          onCancel: () => trackActionDiscard(ActionIds.Create, pageId, DetailsPanelId.PLUGGABLEDATABASES),
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, pageId, DetailsPanelId.PLUGGABLEDATABASES);
        chainFunctions(refresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: !selection.length,
      title: Messages.actions.delete(),
      description: Messages.hints.deletePluggableDBConfirmation(
        selection && selection.length > 0 ? selection[0].name : "",
      ),
      onClick: () => {
        trackActionClick(ActionIds.Delete, pageId, DetailsPanelId.PLUGGABLEDATABASES);
      },
      onConfirm: () => {
        triggerDeletePdb({
          databaseId: selection[0]?.id,
          location,
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            refresh();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, pageId, DetailsPanelId.PLUGGABLEDATABASES),
    },
  ];

  return (
    <Listing
      testId={ListingTestIds.ExaDbPdbs}
      listingComponentRef={setListingComponentRef}
      items={items || []}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemKey={ColumnIds.Name}
      itemsPerPage={perPageItemsCount}
      actionBarItems={dbActions}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      isLoading={loading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Name,
      }}
    />
  );
};
